// src/components/personal/sections/ClientsSection/components/Toolbar.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Download, Settings, RefreshCw, X, Loader } from 'lucide-react';
import Search from './Search';
import Filters from './Filters';
import { ClientFilters } from '../types/types';
import { COMPONENT_STYLES } from '@/theme/components';
import { Flex } from '@/components/ui/Layout';
import { useResponsive } from '@/hooks/useResponsive';

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onExport: () => Promise<void>;
  onOpenSettings: () => void;
  onRefresh: () => void;
  onResetFilters: () => void;
  filters: ClientFilters;
  onFiltersChange: (filters: ClientFilters, shouldRefresh?: boolean) => void;
  disabled?: boolean;
  isFiltering: boolean;
  totalItems?: number;
  isLoading?: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({
  searchQuery,
  onSearchChange,
  showFilters,
  onToggleFilters,
  onExport,
  onOpenSettings,
  onRefresh,
  onResetFilters,
  filters,
  onFiltersChange,
  disabled = false,
  isFiltering,
  totalItems = 0,
  isLoading = false
}) => {
  const { t } = useTranslation(['clients', 'common']);
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Используем хук для определения мобильного устройства
  const { isMobile } = useResponsive();

  // Обработчик изменения фильтров
  const handleFiltersChange = (newFilters: ClientFilters) => {
    if (DEBUG) console.log('Toolbar - вызов handleFiltersChange с фильтрами:', newFilters);
    onFiltersChange(newFilters);
  };

  // Обработчик обновления данных после применения фильтров
  const handleRefreshAfterFilters = () => {
    if (DEBUG) console.log('Toolbar - вызов handleRefreshAfterFilters');
    onRefresh();
  };
  
  // Подсчет активных фильтров для бейджа
  const activeFiltersCount = Object.values(filters).filter(val => val && val !== 'all').length + (searchQuery ? 1 : 0);

  // Функция для рендеринга десктопной версии
  const renderDesktopToolbar = () => (
    <Flex justify="between" align="center" wrap={true} className="w-full">
      <Flex align="center" gap="sm" className="flex-1">
        <Search 
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t('search.placeholder', { defaultValue: "Поиск по ID клиента или телефону" })}
          disabled={isLoading || disabled}
          debounceMs={800}
          searchOnType={false}
          normalizePhone={true}
        />
        
        {/* Кнопка сброса фильтров */}
        {isFiltering && (
          <button
            onClick={onResetFilters}
            className={`${COMPONENT_STYLES.button.text} text-red-500`}
            title={t('toolbar.reset_filters', { defaultValue: "Сбросить фильтры" })}
            disabled={disabled || isLoading}
          >
            <X className={`${COMPONENT_STYLES.icon.action} text-red-500`} />
          </button>
        )}
        
        {/* Отображение общего количества клиентов */}
        {totalItems > 0 && (
          <span className="text-sm text-gray-500 ml-2">
            {isLoading && isFiltering ? (
              <Flex align="center" gap="xs">
                <Loader className="animate-spin h-4 w-4" />
                {t('toolbar.loading_clients', { defaultValue: "Загрузка клиентов..." })}
              </Flex>
            ) : (
              t('toolbar.total_items', { count: totalItems, defaultValue: "Всего: {{count}}" })
            )}
          </span>
        )}
      </Flex>

      <Flex gap="sm" className="mt-2 md:mt-0">
        {/* Кнопка обновления данных */}
        <button
          onClick={onRefresh}
          className={COMPONENT_STYLES.button.toolbarButton}
          disabled={disabled || isLoading}
          title={t('toolbar.refresh_data', { defaultValue: "Обновить данные" })}
        >
          <RefreshCw className={`${COMPONENT_STYLES.icon.button} ${isLoading ? 'animate-spin' : ''}`} />
          {t('toolbar.refresh', { defaultValue: "Обновить" })}
        </button>
        
        {/* Кнопка фильтров */}
        <button
          onClick={onToggleFilters}
          className={`${COMPONENT_STYLES.button.toolbarButton} ${isFiltering ? COMPONENT_STYLES.button.active : ''}`}
          disabled={disabled || isLoading}
        >
          <Filter className={`${COMPONENT_STYLES.icon.button} ${isFiltering ? 'text-primary' : ''}`} />
          {t('toolbar.filters', { defaultValue: "Фильтры" })} 
          {activeFiltersCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-primary text-white rounded-full text-xs">
              {activeFiltersCount}
            </span>
          )}
        </button>
        
        {/* Кнопка настроек */}
        <button
          onClick={onOpenSettings}
          className={COMPONENT_STYLES.button.toolbarButton}
          disabled={disabled || isLoading}
        >
          <Settings className={COMPONENT_STYLES.icon.button} />
          {t('toolbar.settings', { defaultValue: "Настройки" })}
        </button>
        
        {/* Кнопка экспорта */}
        <button 
          onClick={onExport}
          className={COMPONENT_STYLES.button.toolbarButton}
          disabled={disabled || isLoading || (totalItems === 0)}
        >
          <Download className={COMPONENT_STYLES.icon.button} />
          {t('toolbar.export', { defaultValue: "Экспорт" })}
        </button>
      </Flex>
    </Flex>
  );

  // Функция для рендеринга мобильной версии
  const renderMobileToolbar = () => (
    <>
      {/* Поиск сохраняем неизменным */}
      <Flex align="center" className="w-full">
        <Search 
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={t('search.placeholder', { defaultValue: "Поиск по ID клиента или телефону" })}
          disabled={isLoading || disabled}
          debounceMs={800}
          searchOnType={false}
          normalizePhone={true}
        />
        
        {/* Кнопка сброса фильтров */}
        {isFiltering && (
          <button
            onClick={onResetFilters}
            className={`${COMPONENT_STYLES.button.text} text-red-500`}
            title={t('toolbar.reset_filters', { defaultValue: "Сбросить фильтры" })}
            disabled={disabled || isLoading}
          >
            <X className={`${COMPONENT_STYLES.icon.action} text-red-500`} />
          </button>
        )}
      </Flex>

      {/* Вторая строка с инфо и кнопками в один ряд */}
      <div className="flex justify-between items-center w-full mt-2">
        {/* Общее количество */}
        <div className="text-sm font-medium">
          {t('toolbar.total_items_simple', { defaultValue: "Всего:" })} {totalItems}
        </div>

        {/* Кнопки действий в одну строку */}
        <div className="flex">
          {/* Кнопка обновления */}
          <button 
            onClick={onRefresh}
            className="p-2 mx-1 text-gray-600" 
            disabled={disabled || isLoading}
            title={t('toolbar.refresh_data', { defaultValue: "Обновить данные" })}
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          
          {/* Кнопка фильтров */}
          <button 
            onClick={onToggleFilters}
            className={`p-2 mx-1 relative ${isFiltering ? 'text-primary' : 'text-gray-600'}`}
            disabled={disabled || isLoading}
            title={t('toolbar.filters', { defaultValue: "Фильтры" })}
          >
            <Filter className="w-5 h-5" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-primary text-white rounded-full text-xs">
                {activeFiltersCount}
              </span>
            )}
          </button>
          
          {/* Кнопка настроек */}
          <button 
            onClick={onOpenSettings}
            className="p-2 mx-1 text-gray-600"
            disabled={disabled || isLoading}
            title={t('toolbar.settings', { defaultValue: "Настройки" })}
          >
            <Settings className="w-5 h-5" />
          </button>
          
          {/* Кнопка экспорта */}
          <button 
            onClick={onExport}
            className="p-2 mx-1 text-gray-600"
            disabled={disabled || isLoading || (totalItems === 0)}
            title={t('toolbar.export', { defaultValue: "Экспорт" })}
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className={COMPONENT_STYLES.toolbar.container}>
      <Flex direction="column" gap="md">
        {/* Рендерим соответствующий интерфейс в зависимости от типа устройства */}
        {isMobile ? renderMobileToolbar() : renderDesktopToolbar()}

        {/* Панель фильтров остается неизменной */}
        {showFilters && (
          <Filters 
            filters={filters}
            onChange={handleFiltersChange}
            disabled={disabled}
            isLoading={isLoading}
            onReset={onResetFilters}
            onRefresh={handleRefreshAfterFilters}
          />
        )}
      </Flex>
    </div>
  );
};

export default React.memo(Toolbar);